import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PartnerProviderLog } from "@/types/partner-provider-log";
import { formatDateTime } from "@/utils/dateUtils";
import {
  formatDurationMs,
  stringifyLogDetails,
} from "../utils/partnerProviderLogs";

interface PartnerProviderLogDetailsDialogProps {
  log: PartnerProviderLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="text-sm break-words">{value}</div>
    </div>
  );
};

export const PartnerProviderLogDetailsDialog: React.FC<
  PartnerProviderLogDetailsDialogProps
> = ({ log, open, onOpenChange }) => {
  const { t } = useTranslation();

  if (!log) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("partners.admin.logDetailsTitle")}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <DetailRow
            label={t("partners.admin.logColumns.time")}
            value={formatDateTime(log.createdAt)}
          />
          <DetailRow
            label={t("partners.admin.logColumns.provider")}
            value={String(log.provider)}
          />
          <DetailRow
            label={t("partners.admin.logColumns.event")}
            value={t(`partners.admin.eventTypeValues.${log.eventType}`, {
              defaultValue: log.eventType,
            })}
          />
          <DetailRow
            label={t("partners.admin.logColumns.status")}
            value={t(`partners.admin.logStatusValues.${log.status}`, {
              defaultValue: log.status,
            })}
          />
          <DetailRow
            label={t("partners.admin.logColumns.level")}
            value={t(`partners.admin.logLevelValues.${log.level}`, {
              defaultValue: log.level,
            })}
          />
          <DetailRow
            label={t("partners.admin.logColumns.operation")}
            value={log.operation || "-"}
          />
          <DetailRow
            label={t("partners.admin.logColumns.duration")}
            value={formatDurationMs(log.durationMs)}
          />
          <DetailRow
            label={t("partners.admin.logColumns.httpStatus")}
            value={log.httpStatus ?? "-"}
          />
          <DetailRow
            label={t("partners.admin.logDetails.hotelId")}
            value={log.hotelId || "-"}
          />
          <DetailRow
            label={t("partners.admin.logDetails.tenantId")}
            value={log.tenantId || "-"}
          />
          <DetailRow
            label={t("partners.admin.logDetails.errorCode")}
            value={log.errorCode || "-"}
          />
          <DetailRow
            label={t("partners.admin.logColumns.message")}
            value={log.message || "-"}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">
            {t("partners.admin.logDetails.details")}
          </span>
          <pre className="max-h-80 overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap break-words">
            {stringifyLogDetails(log.details)}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
};
